<template>
  <div class="i-email-gate">
    <!-- blurred preview -->
    <div class="preview">
      <div class="preview-inner">
        <div
          v-for="row in previewRows"
          :key="row.label"
          class="preview-row"
        >
          <div class="preview-label">{{ row.label }}</div>
          <div class="preview-bar-wrap">
            <div
              class="preview-bar"
              :style="{ width: row.width + '%' }"
            ></div>
          </div>
          <div class="preview-val">{{ row.value }}</div>
        </div>
      </div>
      <div class="preview-overlay">
        <div class="preview-overlay-label">Your Report is Ready</div>
        <div class="preview-overlay-msg">Enter your email to see your full Career Strategy Report</div>
      </div>
    </div>

    <div class="eyebrow">Your Strategy Report</div>
    <div class="title">Here’s what’s holding you back, and how to fix it.</div>
    <div class="sub">
      Your report includes your market comp positioning, your career capital assessment, and three specific strategic
      recommendations based on your answers.
    </div>

    <div class="input-wrap">
      <input
        v-model="email"
        type="email"
        class="input"
        :class="{ error: error }"
        placeholder="Enter your email"
        @keypress.enter="submit"
      />
    </div>
    <button
      class="btn"
      @click="submit"
    >
      Show my report →
    </button>
    <div class="micro">Your report appears instantly. No spam.</div>
    <div class="micro small">Your data is confidential. Never shared with employers, recruiters, or third parties.</div>
  </div>
</template>

<script setup>
import { ref } from "vue"

const emit = defineEmits(["submit"])

const email = ref("")
const error = ref(false)

const previewRows = [
  { label: "Market Comp (Your Level)", width: 65, value: "$240K" },
  { label: "Your Current Comp", width: 55, value: "$210K" },
  { label: "Career Position Score", width: 72, value: "72/100" }
]

function submit() {
  if (!email.value || !email.value.includes("@")) {
    error.value = true
    return
  }
  error.value = false
  emit("submit", email.value)
}
</script>

<style lang="scss" scoped>
.i-email-gate {
  .preview {
    width: 100%;
    border-radius: 12px;
    overflow: hidden;
    background: #1118270f;
    min-height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    padding: 20px;
    position: relative;

    @include respond-to($breakpoint-md) {
      min-height: 160px;
      padding: 28px;
      margin-bottom: 28px;
    }
  }

  .preview-inner {
    filter: blur(5px);
    opacity: 0.6;
    width: 100%;
  }

  .preview-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #11182714;

    &:last-child {
      border-bottom: none;
    }
  }

  .preview-label {
    font-size: 13px;
    color: #11182799;
    flex: 1;
  }

  .preview-bar-wrap {
    width: 40%;
    height: 8px;
    background: #1118271a;
    border-radius: 4px;
    overflow: hidden;
    margin: 0 8px;

    @include respond-to($breakpoint-sm) {
      width: 50%;
    }
  }

  .preview-bar {
    height: 100%;
    border-radius: 4px;
    background: $amber;
  }

  .preview-val {
    font-size: 13px;
    font-weight: 700;
    color: #111827cc;
    font-family: $serif;
    white-space: nowrap;
    min-width: 48px;
    text-align: right;
  }

  .preview-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #ffffff8c;
    backdrop-filter: blur(2px);
    border-radius: 12px;
  }

  .preview-overlay-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #111827bf;
    margin-bottom: 8px;
  }

  .preview-overlay-msg {
    font-family: $serif;
    font-size: 18px;
    color: $ink;
    text-align: center;
    letter-spacing: -0.01em;
    padding: 0 16px;

    @include respond-to($breakpoint-sm) {
      padding: 0 20px;
    }
  }

  .eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: $amber;
    margin-bottom: 12px;

    @include respond-to($breakpoint-md) {
      margin-bottom: 14px;
    }
  }

  .title {
    font-family: $serif;
    font-size: clamp(22px, 5vw, 32px);
    font-weight: 400;
    letter-spacing: -0.02em;
    color: $ink;
    line-height: 1.2;
    margin-bottom: 10px;
  }

  .sub {
    font-size: 14px;
    color: $ink-60;
    line-height: 1.65;
    margin-bottom: 22px;

    @include respond-to($breakpoint-md) {
      font-size: 15px;
      margin-bottom: 28px;
    }
  }

  .input-wrap {
    margin-bottom: 6px;
  }

  .input {
    width: 100%;
    padding: 13px 16px;
    border: 1.5px solid #11182726;
    border-radius: 8px;
    font-size: 16px;
    font-family: $sans;
    color: #111827e6;
    transition: border-color 0.2s;
    outline: none;
    background: #1118270f;

    @include respond-to($breakpoint-md) {
      padding: 15px 18px;
    }

    &:focus {
      border-color: $amber-mid;
    }

    &.error {
      border-color: #dc2626;
    }
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: $amber-mid;
    color: $white;
    font-family: $sans;
    font-size: 15px;
    font-weight: 600;
    padding: 14px 36px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition:
      opacity 0.2s,
      transform 0.15s;
    width: 100%;
    margin-bottom: 10px;
    margin-top: 6px;

    @include respond-to($breakpoint-md) {
      font-size: 16px;
      padding: 16px 48px;
    }

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
    }
  }

  .micro {
    font-size: 12px;
    color: $slate;
    margin-top: 10px;
    text-align: center;

    .small {
      margin-top: 16px;
      font-size: 11px;
      color: $ink-40;
    }
  }
}
</style>
